/**
 * useDocumentContext — mount-time Word-document classification for
 * Loom/Thread's content-aware greeting. Spec 29 Phase 3 follow-up.
 *
 * Reads the current Word document's outline once via Office.js,
 * passes it through the pure ``classifyDocument`` helper, and
 * exposes the result so the shell's greeting can adapt:
 *
 *   empty          → "Welcome, type /draft"
 *   has-content    → "I see N sections; what would you like to do?"
 *   has-content +  → "Looks like a CER — want to review, update, or
 *   probableDocType  draft fresh?"
 *
 * SRP: owns the one-shot Office.js read + loading-state machine.
 * Does NOT own copy, rendering, or the greeting — the shell
 * composes those from the classification.
 *
 * # DECISION: read once on mount, not on every doc change.
 * # WHY: the greeting is a first-impression surface — it renders
 * #      before the writer has taken any action. Continuously
 * #      re-reading would cost Office.js round-trips on every
 * #      keystroke for minimal value (a writer who clears the
 * #      document mid-chat is an edge case, and the greeting
 * #      won't be visible then anyway — the Timeline has entries).
 * #      One read at mount is honest + cheap.
 * # DATE: 2026-04-23
 *
 * # AMENDMENT (2026-09-08): re-read on an explicit ``refreshNonce`` bump.
 * # WHY: the shell routes ``idle`` + ``classification === "empty"`` to the
 * #      full-screen Home scene, ABOVE the "spine with sections" branch.
 * #      A Load (Home/Drafts card, spine "Load doc") replaces the whole
 * #      body, so a document that was blank at mount stays latched as
 * #      "empty" and any later return to idle drops the writer on Home
 * #      with the TabBar hidden — even though Word is full of the loaded
 * #      run. A Load is a deliberate body replacement, not a keystroke;
 * #      the caller bumps the nonce once after it settles. Still no
 * #      per-keystroke reads.
 */

import { useEffect, useState } from "react";
import { readDocumentOutline } from "../../services/wordWriter";
import { type DocumentClassification, classifyDocument } from "../documentClassifier";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DocumentContextState =
  | { state: "loading" }
  | { state: "unavailable"; reason: string }
  | { state: "ready"; classification: DocumentClassification };

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param refreshNonce — bump to force a re-classification of the current
 *   Word body (after a Load that replaced it). ``0`` / omitted = the
 *   one-shot mount read.
 */
export function useDocumentContext(refreshNonce = 0): DocumentContextState {
  const [ctx, setCtx] = useState<DocumentContextState>({ state: "loading" });

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshNonce is the deliberate re-read trigger
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const outline = await readDocumentOutline();
        if (cancelled) return;
        if (outline === null) {
          // Office.js not present — sandbox / browser preview.
          // Surface this distinctly from "empty" so the caller
          // can fall back to a context-free greeting honestly
          // instead of claiming the doc is empty.
          setCtx({
            state: "unavailable",
            reason: "office_js_unavailable",
          });
          return;
        }
        const classification = classifyDocument(outline);
        setCtx({ state: "ready", classification });
      } catch (err) {
        if (cancelled) return;
        // Read failure — permission, transient Office.js error,
        // etc. Same fallback as unavailable: honest, lets the
        // greeting render its context-free branch.
        const reason = err instanceof Error ? err.message : String(err);
        if (typeof console !== "undefined") {
          console.info("[useDocumentContext] outline read failed — falling back", err);
        }
        setCtx({ state: "unavailable", reason });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshNonce]);

  return ctx;
}
